/**
 * Single source of truth for every environment variable used by the app.
 *
 * CRA inlines `process.env.REACT_APP_*` at build time, so each variable is
 * read with a *static* member access below. Do not read `process.env`
 * anywhere else in the codebase – import `env` from this file instead.
 */

const required = (name: string, value: string | undefined): string => {
  if (value === undefined || value.trim() === '') {
    throw new Error(
      `[env] Missing required environment variable "${name}". ` +
        `Copy .env.example to .env and fill it in, then restart the dev server.`
    );
  }
  return value.trim();
};

const optional = (value: string | undefined, fallback = ''): string =>
  value !== undefined && value.trim() !== '' ? value.trim() : fallback;

/** Removes any trailing slashes so we can safely do `${base}/path`. */
const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

export const env = {
  // API / media
  apiBaseUrl: stripTrailingSlash(
    required('REACT_APP_API_BASE_URL', process.env.REACT_APP_API_BASE_URL)
  ),
  mediaBaseUrl: stripTrailingSlash(
    required('REACT_APP_MEDIA_BASE_URL', process.env.REACT_APP_MEDIA_BASE_URL)
  ),

  // Third-party assets
  heroVideoUrl: required('REACT_APP_HERO_VIDEO_URL', process.env.REACT_APP_HERO_VIDEO_URL),
  promoVideoEmbedUrl: required(
    'REACT_APP_PROMO_VIDEO_EMBED_URL',
    process.env.REACT_APP_PROMO_VIDEO_EMBED_URL
  ),
  defaultAvatarUrl: required(
    'REACT_APP_DEFAULT_AVATAR_URL',
    process.env.REACT_APP_DEFAULT_AVATAR_URL
  ),

  // Business / contact
  companyName: required('REACT_APP_COMPANY_NAME', process.env.REACT_APP_COMPANY_NAME),
  companyAddress: required('REACT_APP_COMPANY_ADDRESS', process.env.REACT_APP_COMPANY_ADDRESS),
  supportEmail: required('REACT_APP_SUPPORT_EMAIL', process.env.REACT_APP_SUPPORT_EMAIL),
  supportPhone: required('REACT_APP_SUPPORT_PHONE', process.env.REACT_APP_SUPPORT_PHONE),
  supportPhoneDisplay: required(
    'REACT_APP_SUPPORT_PHONE_DISPLAY',
    process.env.REACT_APP_SUPPORT_PHONE_DISPLAY
  ),

  // Social (optional)
  instagramUrl: optional(process.env.REACT_APP_INSTAGRAM_URL, '#'),
  facebookUrl: optional(process.env.REACT_APP_FACEBOOK_URL, '#'),
  youtubeUrl: optional(process.env.REACT_APP_YOUTUBE_URL, '#'),

  // Build info
  isProduction: process.env.NODE_ENV === 'production',
} as const;

export type Env = typeof env;
